import { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Paper, Box, Typography, Button, Grid, TextField, IconButton, Stack } from "@mui/material";
import { Download, Edit, Save, Delete, Add } from "@mui/icons-material";
import { usePDF } from "react-to-pdf";

import MDBox from "../../components/MDBox";
import MDTypography from "../../components/MDTypography";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DefaultNavbar from "../../components/TopNavBar/DefaultNavBar";
import AssetCard from "../../components/AssetCard";

// Import the asset data
import phantomNodeImg from "../../assets/images/phantom_node.png";
import spectraFlexImg from "../../assets/images/assetImages/SpectraFlex Adaptive Wear.png";
import morphoGloveImg from "../../assets/images/assetImages/Morpho-Touch Glove.png";
import missionScopeImg from "../../assets/images/assetImages/MissionScope Planner.png";
import swarmImg from "../../assets/images/assetImages/Swarm Intelligence Deployment Suite.png";
import advTactMobileImg from "../../assets/images/assetImages/Advanced Tactical Mobility System.png";
import stingrayImg from "../../assets/images/assetImages/STINGRAY-X — Autonomous Littoral Navigation Beacon (ALNB).png";
import disruptionImg from "../../assets/images/assetImages/Cyber-Physical Disruption Suite (CPDS).png";
import seopImg from "../../assets/images/assetImages/Strategic Effects Orchestration Platform (SEOP).png";
import agsfsImg from "../../assets/images/assetImages/Adaptive Ground Sensing & Fusion System (AGS-FS).png";
import tgtihImg from "../../assets/images/assetImages/Tactical Geospatial & Threat Intelligence Hub (TG-TIH).png";
import iideImg from "../../assets/images/assetImages/Intelligent Information Distribution Engine (IIDE).png";
import sddcmImg from "../../assets/images/assetImages/Secure Device & Data Concealment Module (SDDCM).png";

import assetData from "../../assets/assetJson/assets.json";

const SECTION_DATA = {
    "executive-summary": {
        id: "executive-summary",
        type: "section",
        content: {
            title: "Executive Summary",
            text: "This report provides an analysis of the selected assets and their capabilities."
        }
    },
    "methodology": {
        id: "methodology",
        type: "section",
        content: {
            title: "Methodology",
            text: "The analysis was conducted using a comprehensive evaluation of asset capabilities and performance metrics."
        }
    }
    // Add more sections here as needed
};

const imageDictionary = {
    "Phantom Node": phantomNodeImg,
    "SpectraFlex Adaptive Wear": spectraFlexImg,
    "Morpho-Touch Glove": morphoGloveImg,
    "MissionScope Planner": missionScopeImg,
    "Swarm Intelligence Deployment Suite": swarmImg,
    "Advanced Tactical Mobility System": advTactMobileImg,
    "STINGRAY-X — Autonomous Littoral Navigation Beacon (ALNB)": stingrayImg,
    "Cyber-Physical Disruption Suite (CPDS)": disruptionImg,
    "Strategic Effects Orchestration Platform (SEOP)": seopImg,
    "Adaptive Ground Sensing & Fusion System (AGS-FS)": agsfsImg,
    "Tactical Geospatial & Threat Intelligence Hub (TG-TIH)": tgtihImg,
    "Intelligent Information Distribution Engine (IIDE)": iideImg,
    "Secure Device & Data Concealment Module (SDDCM)": sddcmImg
}

function ReportGenerator() {
    const [reportItems, setReportItems] = useState([]);
    const [availableItems, setAvailableItems] = useState(() => {
        // Format assets data to include required fields
        const formattedAssets = assetData.map(asset => ({
            ...asset,
            id: `asset-${asset.title.toLowerCase().replace(/\s+/g, '-')}`,
            type: "asset"
        }));
        
        const sections = Object.values(SECTION_DATA);
        return [...sections, ...formattedAssets];
    });
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState("");
    const [newSectionTitle, setNewSectionTitle] = useState("");
    const [isCreatingSection, setIsCreatingSection] = useState(false);

    const { toPDF, targetRef } = usePDF({
        filename: 'report.pdf',
        page: {
            format: 'a4',
            orientation: 'portrait',
        },
        overrides: {
            pdf: {
                compress: true,
                margin: 20,
            },
        },
    });

    const handleDragEnd = (result) => {
        const { source, destination } = result;

        // Drop outside a droppable area
        if (!destination) return;

        // Drop in the same position
        if (
            source.droppableId === destination.droppableId &&
            source.index === destination.index
        ) return;

        // Moving from available items to report
        if (source.droppableId === "available" && destination.droppableId === "report") {
            const item = availableItems[source.index];
            setAvailableItems(prev => prev.filter((_, index) => index !== source.index));
            setReportItems(prev => {
                const newItems = [...prev];
                newItems.splice(destination.index, 0, item);
                return newItems;
            });
        }
        // Moving from report to available items
        else if (source.droppableId === "report" && destination.droppableId === "available") {
            const item = reportItems[source.index];
            setReportItems(prev => prev.filter((_, index) => index !== source.index));
            setAvailableItems(prev => {
                const newItems = [...prev];
                newItems.splice(destination.index, 0, item);
                return newItems;
            });
        }
        // Reordering within the same list
        else if (source.droppableId === destination.droppableId) {
            const items = source.droppableId === "report" ? [...reportItems] : [...availableItems];
            const [removed] = items.splice(source.index, 1);
            items.splice(destination.index, 0, removed);
            
            if (source.droppableId === "report") {
                setReportItems(items);
            } else {
                setAvailableItems(items);
            }
        }
    };

    const handleCreateSection = () => {
        if (!newSectionTitle.trim() && !editText.trim()) return;

        const newSection = {
            id: `section-${Date.now()}`,
            type: "section",
            content: {
                title: newSectionTitle.trim(),
                text: editText.trim()
            }
        };

        setAvailableItems(prev => [...prev, newSection]);
        setNewSectionTitle("");
        setEditText("");
        setIsCreatingSection(false);
    };

    const handleEditStart = (item) => {
        setEditingId(item.id);
        setEditText(item.content.text || "");
        setNewSectionTitle(item.content.title || "");
    };

    const handleEditSave = () => {
        setReportItems(prev => prev.map(item => 
            item.id === editingId 
                ? { 
                    ...item, 
                    content: { 
                        ...item.content, 
                        text: editText,
                        title: newSectionTitle
                    } 
                }
                : item
        ));
        setEditingId(null);
        setNewSectionTitle("");
    };

    const handleEditCancel = () => {
        setEditingId(null);
    };

    const handleDelete = (itemId) => {
        setReportItems(prev => prev.filter(item => item.id !== itemId));
    };

    const renderItem = (item, provided, snapshot, inReportArea = false) => {
        if (item.type === "asset") {
            return (
                <Box 
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    sx={{ 
                        opacity: snapshot.isDragging ? 0.8 : 1,
                        cursor: 'grab',
                        '&:active': {
                            cursor: 'grabbing'
                        },
                        mb: 2  // Add margin bottom for spacing
                    }}
                >
                    {inReportArea ? (
                        // Full asset card for Report Content area
                        <Paper sx={{ p: 2, bgcolor: 'white !important' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={3}>
                                    <Box
                                        component="img"
                                        src={imageDictionary[item.title]}
                                        alt={item.title}
                                        sx={{
                                            width: '100%',
                                            height: 'auto',
                                            objectFit: 'cover',
                                            borderRadius: 1
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={9}>
                                    <Stack spacing={2}>
                                        <Typography variant="h6">{item.title}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {item.description}
                                        </Typography>
                                        
                                        <Box>
                                            <Typography variant="subtitle2" gutterBottom>Specifications</Typography>
                                            <Grid container spacing={2}>
                                                {item.specs.map((spec, index) => (
                                                    <Grid item xs={6} key={index}>
                                                        <Typography variant="body2">
                                                            <strong>{spec.header}:</strong> {spec.description}
                                                        </Typography>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>

                                        <Box>
                                            <Typography variant="subtitle2" gutterBottom>Capabilities</Typography>
                                            <Grid container spacing={2}>
                                                {item.capabilities.map((cap, index) => (
                                                    <Grid item xs={12} key={index}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body2" sx={{ minWidth: 150 }}>
                                                                {cap.main}:
                                                            </Typography>
                                                            <Box sx={{ flexGrow: 1, height: 8, bgcolor: 'grey.200', borderRadius: 4, overflow: 'hidden' }}>
                                                                <Box
                                                                    sx={{
                                                                        height: '100%',
                                                                        bgcolor: 'primary.main',
                                                                        width: `${cap.value}%`,
                                                                        transition: 'width 0.3s ease'
                                                                    }}
                                                                />
                                                            </Box>
                                                            <Typography variant="body2" sx={{ minWidth: 40 }}>
                                                                {cap.value}%
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Paper>
                    ) : (
                        // Collapsed asset card for Available Items area
                        <Paper sx={{ p: 2, bgcolor: 'white !important' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                    component="img"
                                    src={imageDictionary[item.title]}
                                    alt={item.title}
                                    sx={{
                                        width: '60px',
                                        height: '60px',
                                        objectFit: 'cover',
                                        borderRadius: 1
                                    }}
                                />
                                <Typography variant="subtitle1">{item.title}</Typography>
                            </Box>
                        </Paper>
                    )}
                </Box>
            );
        } else if (item.type === "section") {
            return (
                <Box
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    sx={{ 
                        opacity: snapshot.isDragging ? 0.8 : 1,
                        cursor: 'grab',
                        '&:active': {
                            cursor: 'grabbing'
                        },
                        mb: 2  // Add margin bottom for spacing
                    }}
                >
                    {renderEditableSection(item)}
                </Box>
            );
        }
        return null;
    };

    const renderEditableSection = (item) => {
        if (editingId === item.id) {
            return (
                <Paper sx={{ p: 2, mb: 2, bgcolor: 'white !important' }}>
                    <TextField
                        fullWidth
                        label="Section Title (Optional)"
                        value={newSectionTitle}
                        onChange={(e) => setNewSectionTitle(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Section Text"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                            size="small"
                            onClick={handleEditCancel}
                            sx={{ color: 'text.secondary' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={handleEditSave}
                            sx={{
                                bgcolor: "#2B5587",
                                color: "#FFFFFF !important",
                                '&:hover': {
                                    bgcolor: '#3a6ca8',
                                },
                            }}
                        >
                            Save
                        </Button>
                    </Box>
                </Paper>
            );
        }

        return (
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'white !important' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    {item.content.title && (
                        <Typography variant="h6">{item.content.title}</Typography>
                    )}
                    <Box>
                        <IconButton
                            size="small"
                            onClick={() => handleEditStart(item)}
                            sx={{ color: 'text.secondary' }}
                        >
                            <Edit />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => handleDelete(item.id)}
                            sx={{ color: 'text.secondary' }}
                        >
                            <Delete />
                        </IconButton>
                    </Box>
                </Box>
                <Typography>{item.content.text}</Typography>
            </Paper>
        );
    };

    const handleDownloadPDF = async () => {
        try {
            await toPDF();
        } catch (error) {
            console.error('Error generating PDF:', error);
            // You might want to show an error message to the user here
        }
    };

    return (
        <DashboardLayout>
            <DefaultNavbar />
            <MDBox>
                <Paper sx={{ p: 2, mb: 2 }}>
                    <MDTypography variant="h5" gutterBottom>
                        Report Generator
                    </MDTypography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => setIsCreatingSection(true)}
                        >
                            Add Text Section
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Download />}
                            onClick={handleDownloadPDF}
                            disabled={reportItems.length === 0}
                        >
                            Download PDF
                        </Button>
                    </Box>
                </Paper>

                {isCreatingSection && (
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <MDTypography variant="h6" gutterBottom>
                            Create New Text Section
                        </MDTypography>
                        <TextField
                            fullWidth
                            label="Section Title (Optional)"
                            value={newSectionTitle}
                            onChange={(e) => setNewSectionTitle(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Section Text"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                                size="small"
                                onClick={() => {
                                    setIsCreatingSection(false);
                                    setNewSectionTitle("");
                                    setEditText("");
                                }}
                                sx={{ color: 'text.secondary' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleCreateSection}
                                disabled={!editText.trim()}
                                sx={{
                                    bgcolor: "#2B5587",
                                    color: "#FFFFFF !important",
                                    '&:hover': {
                                        bgcolor: '#3a6ca8',
                                    },
                                }}
                            >
                                Create Section
                            </Button>
                        </Box>
                    </Paper>
                )}

                <DragDropContext onDragEnd={handleDragEnd}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 2 }}>
                                <MDTypography variant="h6" gutterBottom>
                                    Available Items
                                </MDTypography>
                                <Droppable droppableId="available">
                                    {(provided) => (
                                        <Box
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            sx={{ 
                                                minHeight: '200px',
                                                p: 2,
                                                bgcolor: 'navy.main',
                                                borderRadius: 2
                                            }}
                                        >
                                            {availableItems.map((item, index) => (
                                                <Draggable
                                                    key={item.id}
                                                    draggableId={item.id}
                                                    index={index}
                                                >
                                                    {(provided, snapshot) => renderItem(item, provided, snapshot, false)}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </Box>
                                    )}
                                </Droppable>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <Paper sx={{ p: 2 }}>
                                <MDTypography variant="h6" gutterBottom>
                                    Report Content
                                </MDTypography>
                                <Droppable droppableId="report">
                                    {(provided) => (
                                        <Box
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            sx={{ 
                                                minHeight: '200px',
                                                p: 2,
                                                bgcolor: 'navy.main',
                                                borderRadius: 2
                                            }}
                                        >
                                            <Box ref={targetRef}>
                                                {reportItems.map((item, index) => (
                                                    <Draggable
                                                        key={item.id}
                                                        draggableId={item.id}
                                                        index={index}
                                                    >
                                                        {(provided, snapshot) => renderItem(item, provided, snapshot, true)}
                                                    </Draggable>
                                                ))}
                                            </Box>
                                            {provided.placeholder}
                                        </Box>
                                    )}
                                </Droppable>
                            </Paper>
                        </Grid>
                    </Grid>
                </DragDropContext>
            </MDBox>
        </DashboardLayout>
    );
}

export default ReportGenerator; 